import React from "react";

import type { ChipColor } from "./ColorChip";
import { ColorChip } from "./ColorChip";

/**
 * Defines the available status color types
 */
export type StatusType = ChipColor;

/**
 * Defines all possible status keys for consistent mapping
 */
export type StatusKey =
  | "active"
  | "admin"
  | "approved"
  | "area"
  | "cancelled"
  | "case_worker"
  | "caseworker"
  | "claimed"
  | "completed"
  | "contributor"
  | "coordinator"
  | "critical"
  | "executive_admin"
  | "expired"
  | "guest"
  | "high"
  | "in_progress"
  | "inactive"
  | "low"
  | "manager"
  | "medium"
  | "open"
  | "organization_admin"
  | "partial"
  | "pending"
  | "pending_approval"
  | "processing"
  | "rejected"
  | "reviewing"
  | "supervisor"
  | "verified"
  | "volunteer";

interface StatusChipProps {
  /**
   * The status string. If status is provided, we will try to map it to a color.
   * If both status and color are provided, color takes precedence.
   */
  status?: string;
  className?: string;
  color?: StatusType;
  variant?: "dot" | "solid" | "bordered" | "flat" | "shadow";
  children?: React.ReactNode;
  "aria-label"?: string;
  role?: string;
  [key: string]: any; // Allow additional props
}

/**
 * Maps common status terms to semantic or common colors
 */
function getStatusColor(status: string): StatusType {
  // Normalize the status string by converting to lowercase and removing spaces
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, "_");

  const statusMap: Record<StatusKey, StatusType> = {
    // Status and role mappings in alphabetical order
    active: "success",
    admin: "primary",
    approved: "green",
    area: "blue",
    cancelled: "danger",
    case_worker: "cyan",
    caseworker: "cyan",
    claimed: "blue",
    completed: "success",
    contributor: "green",
    coordinator: "pink",
    critical: "danger",
    executive_admin: "primary",
    expired: "danger",
    guest: "default",
    high: "danger",
    in_progress: "secondary",
    inactive: "danger",
    low: "success",
    manager: "secondary",
    medium: "warning",
    open: "warning",
    organization_admin: "purple",
    partial: "yellow",
    pending: "warning",
    pending_approval: "warning",
    processing: "blue",
    rejected: "red",
    reviewing: "purple",
    supervisor: "zinc",
    verified: "green",
    volunteer: "cyan",
  };

  // Check for the normalized status first, then original lowercase
  // Use type assertion to check if the normalized status is a valid key
  return (
    statusMap[normalizedStatus as StatusKey] ??
    statusMap[status.toLowerCase() as StatusKey] ??
    "default"
  );
}

function resolveChipColor(
  color: StatusType | undefined,
  status: string | undefined,
): StatusType {
  if (color) return color;
  if (status) return getStatusColor(String(status));

  return "default";
}

function resolveChipAriaLabel(
  ariaLabel: string | undefined,
  status: string | undefined,
): string | undefined {
  return (
    ariaLabel ?? (status ? `Status: ${formatStatusLabel(status)}` : undefined)
  );
}

function resolveChipClassName(
  variant: string,
  className: string | undefined,
): string {
  const noBorder = variant === "dot" ? "border-none" : "";

  return `${noBorder} capitalize ${className ?? ""}`;
}

function formatStatusLabel(status: string): string {
  return status.replace(/_/g, " ");
}

// Create a simpler StatusChip component without using forwardRef
export const StatusChip = ({
  "aria-label": ariaLabel,
  children,
  className,
  color,
  role = "status",
  status,
  variant = "solid",
  ...props
}: StatusChipProps) => {
  return (
    <ColorChip
      aria-label={resolveChipAriaLabel(ariaLabel, status)}
      className={resolveChipClassName(variant, className)}
      color={resolveChipColor(color, status)}
      role={role}
      variant={variant}
      {...props}
    >
      {children ?? (status ? formatStatusLabel(status) : undefined)}
    </ColorChip>
  );
};

StatusChip.displayName = "StatusChip";
